import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Video } from '@eneco/components/Video/Video';
import { StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Card, InputUsage, Stack, Text, TextLink } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SolarPanelsRedeliveryStepRendering } from '@sitecore/types/SolarPanelsRedeliveryStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { SolarPanelsOutputCalculatorForm } from '../../components';
import { useFlowNavigation } from '../../hooks';

interface FormFields {
  solarPanelsOutput: number;
}

export const SolarPanelsRedeliveryStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const {
    context: { solarPanelsOutput },
  } = flowState;

  const { SolarPanelsRedeliveryStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SolarPanelsRedeliveryStep: SolarPanelsRedeliveryStepRendering;
  }>();

  const solarOutputField = textualData?.redelivery?.solarPanelsOutputFormField?.value;
  const solarPanelsDialog = textualData?.redeliveryEstimateModal?.solarPanelsQuantityDialog?.value;
  const redeliveryVideo = textualData?.redeliveryInformationVideo;

  const formSchema = useMemo(
    () =>
      yup.object({
        solarPanelsOutput: yup
          .number()
          .integer(solarOutputField?.validationMessage)
          .typeError(solarOutputField?.validationMessage ?? '')
          .required(solarOutputField?.requiredMessage)
          .min(1, solarOutputField?.validationMinValueMessage)
          .max(99999, solarOutputField?.validationMaxValueMessage),
      }),
    [solarOutputField],
  );

  const {
    handleSubmit,
    getValues,
    setValue,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: { solarPanelsOutput },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleCalculatorConfirm = useCallback(
    (output: number) => {
      setValue('solarPanelsOutput', output);
      setIsModalOpen(false);
    },
    [setValue],
  );

  useEffect(() => {
    if (!isSubmitSuccessful) return;
    const formData = getValues();
    updateContextAndGoToNextStep({
      solarPanelsOutput: Number(formData.solarPanelsOutput) || undefined,
    });
  }, [isSubmitSuccessful, getValues, updateContextAndGoToNextStep]);

  return (
    <>
      <Layout.Content variant="A" handleSubmit={handleSubmit}>
        <StepHeader textualData={textualData} />

        <Stack.Item>
          <InputUsage
            {...register('solarPanelsOutput')}
            label={solarOutputField?.label ?? ''}
            error={errors.solarPanelsOutput?.message}
            type="redelivery"
            unit="kWh"
            hint={solarOutputField?.hint}
          />
          <Box paddingTop="3">
            <Text size="BodyS" color="textLowEmphasis">
              <TextLink onClick={openModal}>{solarPanelsDialog?.triggerText}</TextLink>
            </Text>
          </Box>
        </Stack.Item>

        {redeliveryVideo?.youtubeVideoIdText?.value && (
          <Card corners="rounded" overflow="hidden">
            <Video
              title={redeliveryVideo?.youtubeVideoTitle?.value}
              videoId={redeliveryVideo.youtubeVideoIdText.value}
            />
          </Card>
        )}

        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
      </Layout.Content>

      <TrackedDialog isOpen={isModalOpen} setOpen={setIsModalOpen} title={solarPanelsDialog?.title}>
        <RichText html={solarPanelsDialog?.content} />
        <Box paddingTop="4">
          <SolarPanelsOutputCalculatorForm
            textualData={textualData}
            handleAction={handleCalculatorConfirm}
            handleCancel={closeModal}
          />
        </Box>
      </TrackedDialog>
    </>
  );
};
